/**
 * Events for Tandem, target DtApp (DtFacility.eventTarget)
 */

// Types used in event payloads.
/** @import { DtModel } from "./loader/DtVizLoad" */

/**
 * Fired when a loaded DtFacility has some updates, made by concurrent client
 * @event Autodesk.Viewing.Private#DT_FACILITY_CHANGED_EVENT
 */
export const DT_FACILITY_CHANGED_EVENT = 'dtFacilityChanged';

/**
 * Fired when a loaded DtModel has some updates, made by concurrent client
 * @event Autodesk.Viewing.Private#DT_MODEL_CHANGED_EVENT
 */
export const DT_MODEL_CHANGED_EVENT = 'dtModelChanged';
/**
 * Fired when a loaded DTModel has some metadata updates, made by concurrent client
 * @event Autodesk.Viewing.Private#DT_MODEL_METADATA_CHANGED_EVENT
 */
export const DT_MODEL_METADATA_CHANGED_EVENT = 'dtModelMetaDataChanged';

/**
 * Fired when the facets changed (based on model updates or filtering)
 * @event Autodesk.Viewing.Private#DT_FACETS_UPDATED
 */
export const DT_FACETS_UPDATED = 'dtFacetsUpdated';

/**
 * Fired when the facets manager has loaded all the facets
 * @event Autodesk.Viewing.Private#DT_FACETS_LOADED
 */
export const DT_FACETS_LOADED = 'dtFacetsLoaded';

/**
 * Fired when the facets are reset
 * @event Autodesk.Viewing.Private#DT_FACETS_RESET
 */
export const DT_FACETS_RESET = 'dtFacetsReset';

/**
 * Fired when the currently marked active team changed
 * @event Autodesk.Viewing.Private#DT_ACTIVE_TEAM_CHANGED_EVENT
 */
export const DT_ACTIVE_TEAM_CHANGED_EVENT = 'dtActiveTeamChanged';
/**
 * Fired when the team list has changed
 * @event Autodesk.Viewing.Private#DT_TEAMS_CHANGED_EVENT
 */
export const DT_TEAMS_CHANGED_EVENT = 'dtTeamsChanged';
/**
 * Fired when the list of users in a particular team has changed
 * @event Autodesk.Viewing.Private#DT_TEAM_USERS_CHANGED_EVENT
 */
export const DT_TEAM_USERS_CHANGED_EVENT = 'dtTeamUsersChanged';
/**
 * Fired when the list of facilities in a particular team has changed
 * @event Autodesk.Viewing.Private#DT_TEAM_FACILITIES_CHANGED_EVENT
 */
export const DT_TEAM_FACILITIES_CHANGED_EVENT = 'dtTeamFacilitiesChanged';
/**
 * Fired when the list of facilities shared with the current user has changed
 * @event Autodesk.Viewing.Private#DT_SHARED_FACILITIES_CHANGED_EVENT
 */
export const DT_SHARED_FACILITIES_CHANGED_EVENT = 'dtUserFacilitiesChanged';
/**
 * Fired when the list of facilities shared with the current user has changed
 * @event Autodesk.Viewing.Private#DT_USER_FACILITIES_CHANGED_EVENT
 * @deprecated Use DT_SHARED_FACILITIES_CHANGED_EVENT instead
 */
export const DT_USER_FACILITIES_CHANGED_EVENT = DT_SHARED_FACILITIES_CHANGED_EVENT;

/**
 * Fired when a team property has changed
 * @event Autodesk.Viewing.Private#DT_TEAM_CHANGED_EVENT
 */
export const DT_TEAM_CHANGED_EVENT = 'dtTeamChanged';

/**
 * Fired when the list of documents in a facility has changed
 * @event Autodesk.Viewing.Private#DT_DOCUMENTS_CHANGED_EVENT
 */
export const DT_DOCUMENTS_CHANGED_EVENT = 'dtDocumentsChanged';

/**
 * Fired when the primary model's metadata is loaded and the 3d model
 * space transformations have been derived from that model
 */
export const DT_PRIMARY_MODEL_LOADED = 'dtPrimaryModelLoaded';

/**
 * Fired when message websocket connection is restored
 */
export const DT_MSG_WS_RECONNECTED = 'dtMsgWsReconnected';

/**
 * Fired when a stream marker is clicked
 */
export const DT_STREAM_MARKER_CLICKED = 'dtStreamMarkerClicked';

/**
 * Fired when the cursor enters a stream marker
 */
export const DT_STREAM_MARKER_MOUSE_OVER = 'dtStreamMarkerMouseOver';

/**
 * Fired when the cursor exits a stream marker
 */
export const DT_STREAM_MARKER_MOUSE_OUT = 'dtStreamMarkerMouseOut';

/** Stream markers visibility or configuration changed */
export const DT_STREAM_MARKER_CHANGED_EVENT = 'dtStreamMarkerChanged';

/**
 * Fired when stream info changed
 */
export const DT_STREAMS_INFO_CHANGED_EVENT = 'dtStreamsInfoChanged';

/**
 * Fired when stream last readings changed
 */
export const DT_STREAMS_LAST_READINGS_CHANGED_EVENT = 'dtStreamsLastReadingsChanged';

/**
 * Fired when stream alerts changed
 */
export const DT_STREAM_ALERTS_CHANGED_EVENT = 'dtStreamAlertsChanged';

/**
 * Fired when new stream events data is available in cache
 */
export const DT_STREAM_EVENTS_DATA_EVENT = 'dtStreamEventsData';

/**
 * Fired when systems info changed
 */
export const DT_SYSTEMS_CHANGED_EVENT = 'dtSystemsChanged';

/**
 * Fired when a system's connections changed
 */
export const DT_SYSTEM_CONNECTIONS_CHANGED_EVENT = 'dtSystemConnectionsChanged';

/** Heatmap visibility or configuration changed */
export const DT_HEATMAP_CHANGED_EVENT = 'dtHeatmapChanged';

/**
 * Fired when the current view changed
 */
export const DT_CURRENT_VIEW_CHANGED_EVENT = 'dtCurrentViewChanged';
/**
 * Fired when the list of views of a facility changes
 */
export const DT_FACILITY_VIEWS_CHANGED_EVENT = 'dtFacilityViewChanged';

/** Timeline configuration or underlying data changed. */
export const DT_TELEMETRY_CHANGED_EVENT = 'dtTelemetryChangedEvt';

/** Timeline regions of non-compliance changed. */
export const DT_TELEMETRY_REGIONS_EVENT = 'dtTelemetryRegionsEvt';

/** Centralized "ticker" for synchronizing telemetry view observers. */
export const DT_TELEMETRY_TICK_EVENT = 'dtTelemetryTickEvt';

/** Readings for the Timeline's instant (selected) time have changed.  */
export const DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT = 'dtTelemetryInstantChangedEvt';

/** Fired when a facility user changed */
export const DT_FACILITY_USER_CHANGED_EVENT = 'dtFacilityUserChanged';

/** When DataViz visibility changes, either stream pins or heatmap */
export const DT_DATA_VISIBILITY_CHANGED_EVENT = 'dtDataVisibilityChanged';

/** When sketch tool mode changes, including turning off */
export const DT_SKETCH_TOOL_CHANGED_EVENT = 'dtSketchToolChanged';

/** When sketch tool encounters an error */
export const DT_SKETCH_TOOL_ERROR_EVENT = 'dtSketchToolError';

/** System tracing events */
export const DT_TRACE_ELEMENTS_CHANGED_EVENT = 'DT_TRACE_ELEMENTS_CHANGED_EVENT';
export const DT_TRACE_DRAG_BEGIN = 'DT_TRACE_DRAG_BEGIN';
export const DT_TRACE_DRAG_HOVER = 'DT_TRACE_DRAG_HOVER';
export const DT_TRACE_DRAG_STOP = 'DT_TRACE_DRAG_STOP';

/** HUD layers ui events */
export const DT_HUD_HOVERED_ASSET_EVENT = 'DT_HUD_HOVERED_ASSET_EVENT';
export const DT_HUD_UNHOVERED_ASSET_EVENT = 'DT_HUD_UNHOVERED_ASSET_EVENT';

/** When the theming for a scope of elements changed. */
export const DT_THEMING_CHANGED_EVENT = 'dtThemingChanged';

/**
 * Fired through the viewer instance.
 * When a room fragment was loaded (incl. geom and material).
 * @property {DtModel} model The model instance that the fragment belongs to.
 * @property {Number} fragId The id of the fragment.
 */
export const DT_ROOM_FRAGMENT_LOADED_EVENT = 'dtRoomFragmentLoaded';

/**
 * Fired through the viewer instance.
 * When the loader goes idle, i.e. all previously queued up geometries have been loaded.
 */
export const DT_LOADER_IDLE_EVENT = 'dtLoaderIdle';